/**
 * Permission Constants
 * Maps all backend permissions for frontend RBAC
 * Based on backend permissions_data structure
 */

export const PERMISSIONS = {
    // Wildcard permission for super admins
    WILDCARD: '*',

    // PASCO permissions
    PASCO_READ: 'pasco:read',
    PASCO_SEARCH: 'pasco:search',
    PASCO_UPLOAD: 'pasco:upload',
    PASCO_EDIT: 'pasco:edit',
    PASCO_DELETE: 'pasco:delete',
    PASCO_APPROVE: 'pasco:approve',

    // User permissions
    USER_READ: 'user:read',
    USER_CREATE: 'user:create',
    USER_UPDATE: 'user:update',
    USER_DELETE: 'user:delete',
    USER_BAN: 'user:ban',
    USER_ASSIGN_ROLE: 'user:assign_role',

    // Role permissions
    ROLE_READ: 'role:read',
    ROLE_CREATE: 'role:create',
    ROLE_UPDATE: 'role:update',
    ROLE_DELETE: 'role:delete',
    ROLE_ASSIGN_PERMISSION: 'role:assign_permission',

    // Permission permissions
    PERMISSION_READ: 'permission:read',
    PERMISSION_UPDATE: 'permission:update',

    // Task permissions
    TASK_READ: 'task:read',
    TASK_UPDATE: 'task:update',
    TASK_DELETE: 'task:delete',

    // Stats permissions
    STATS_READ: 'stats:read',

    // University permissions
    UNIVERSITY_READ: 'university:read',
    UNIVERSITY_CREATE: 'university:create',
    UNIVERSITY_UPDATE: 'university:update',
    UNIVERSITY_DELETE: 'university:delete',
} as const;

// Type for all permission values
export type Permission = typeof PERMISSIONS[keyof typeof PERMISSIONS];

/**
 * Permission descriptions for UI display
 */
export const PERMISSION_DESCRIPTIONS: Record<Permission, string> = {
    [PERMISSIONS.WILDCARD]: 'Full system access - all permissions',

    // PASCO
    [PERMISSIONS.PASCO_READ]: 'View PASCO documents',
    [PERMISSIONS.PASCO_SEARCH]: 'Search PASCO documents',
    [PERMISSIONS.PASCO_UPLOAD]: 'Upload and create PASCO documents',
    [PERMISSIONS.PASCO_EDIT]: 'Edit existing PASCO documents',
    [PERMISSIONS.PASCO_DELETE]: 'Delete PASCO documents',
    [PERMISSIONS.PASCO_APPROVE]: 'Approve PASCO documents',

    // User
    [PERMISSIONS.USER_READ]: 'View users',
    [PERMISSIONS.USER_CREATE]: 'Create users',
    [PERMISSIONS.USER_UPDATE]: 'Update users',
    [PERMISSIONS.USER_DELETE]: 'Delete users',
    [PERMISSIONS.USER_BAN]: 'Ban/unban users',
    [PERMISSIONS.USER_ASSIGN_ROLE]: 'Assign roles to users',

    // Role
    [PERMISSIONS.ROLE_READ]: 'View roles',
    [PERMISSIONS.ROLE_CREATE]: 'Create roles',
    [PERMISSIONS.ROLE_UPDATE]: 'Update roles',
    [PERMISSIONS.ROLE_DELETE]: 'Delete roles',
    [PERMISSIONS.ROLE_ASSIGN_PERMISSION]: 'Assign permissions to roles',

    // Permission
    [PERMISSIONS.PERMISSION_READ]: 'View permissions',
    [PERMISSIONS.PERMISSION_UPDATE]: 'Update permission descriptions',

    // Task
    [PERMISSIONS.TASK_READ]: 'View tasks',
    [PERMISSIONS.TASK_UPDATE]: 'Update task status',
    [PERMISSIONS.TASK_DELETE]: 'Delete tasks',

    // Stats
    [PERMISSIONS.STATS_READ]: 'View system statistics',

    // University
    [PERMISSIONS.UNIVERSITY_READ]: 'View universities',
    [PERMISSIONS.UNIVERSITY_CREATE]: 'Create universities',
    [PERMISSIONS.UNIVERSITY_UPDATE]: 'Update universities',
    [PERMISSIONS.UNIVERSITY_DELETE]: 'Delete universities',
};

/**
 * Permission resource groups for easier management
 */
export const PERMISSION_GROUPS = {
    PASCO: [
        PERMISSIONS.PASCO_READ,
        PERMISSIONS.PASCO_SEARCH,
        PERMISSIONS.PASCO_UPLOAD,
        PERMISSIONS.PASCO_EDIT,
        PERMISSIONS.PASCO_DELETE,
        PERMISSIONS.PASCO_APPROVE,
    ],
    USER: [
        PERMISSIONS.USER_READ,
        PERMISSIONS.USER_CREATE,
        PERMISSIONS.USER_UPDATE,
        PERMISSIONS.USER_DELETE,
        PERMISSIONS.USER_BAN,
        PERMISSIONS.USER_ASSIGN_ROLE,
    ],
    ROLE: [
        PERMISSIONS.ROLE_READ,
        PERMISSIONS.ROLE_CREATE,
        PERMISSIONS.ROLE_UPDATE,
        PERMISSIONS.ROLE_DELETE,
        PERMISSIONS.ROLE_ASSIGN_PERMISSION,
    ],
    PERMISSION: [
        PERMISSIONS.PERMISSION_READ,
        PERMISSIONS.PERMISSION_UPDATE,
    ],
    TASK: [
        PERMISSIONS.TASK_READ,
        PERMISSIONS.TASK_UPDATE,
        PERMISSIONS.TASK_DELETE,
    ],
    STATS: [
        PERMISSIONS.STATS_READ,
    ],
    UNIVERSITY: [
        PERMISSIONS.UNIVERSITY_READ,
        PERMISSIONS.UNIVERSITY_CREATE,
        PERMISSIONS.UNIVERSITY_UPDATE,
        PERMISSIONS.UNIVERSITY_DELETE,
    ],
} as const;
